import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createOpportunity,
  deleteOpportunity,
  updateOpportunity,
  type CreateOpportunityParams,
  type UpdateOpportunityParams,
} from "./api"

interface CreateOpportunityMutationParams {
  opportunity: CreateOpportunityParams
  overrideBookId?: string
}

export function useCreateOpportunity(pipelineId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ opportunity, overrideBookId }: CreateOpportunityMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return createOpportunity(context, pipelineId, opportunity, overrideBookId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline", pipelineId, "opportunities"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["contact"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}

export function useUpdateOpportunity(pipelineId: string, bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...params }: UpdateOpportunityParams & { id: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return updateOpportunity(context, pipelineId, id, params)
    },
    onSuccess: (_opportunity, variables) => {
      if (variables.pipeline_id && variables.pipeline_id !== pipelineId) {
        queryClient.invalidateQueries({ queryKey: ["pipeline", pipelineId] })
        queryClient.invalidateQueries({ queryKey: ["pipeline", variables.pipeline_id] })
        queryClient.invalidateQueries({ queryKey: ["pipelines"] })
      } else {
        queryClient.invalidateQueries({ queryKey: ["pipeline", pipelineId, "opportunities"] })
      }
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["contact"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}

export function useDeleteOpportunity(pipelineId: string, bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (opportunityId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return deleteOpportunity(context, pipelineId, opportunityId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline", pipelineId, "opportunities"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["contact"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}
